import React, { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Col, Row, Button } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import 'boxicons';

import { useAuth } from 'hooks/useAuth';
import { getFirebaseBackend } from '../../helpers/firebaseHelper';
import PageStructure from '../../components/Page-Structure';
import SuccessModal from '../../components/Success-Modal';
import LabelGroup from '../../components/Label-Group';
import TextInput from '../../components/Text-Input';
import SwitchInput from '../../components/Switch-Input';
import SliderInput from '../../components/Slider-Input';
import ShowComponent from '../../components/Show-Component';
import QuestionsInput from 'components/Questions-Input';

const formConfig = {
  defaultValues: {
    name: '',
    passPercent: false,
    percent: 0,
    scrambleQuestions: false,
    answerKey: false,
    questions: [],
  },
  shouldFocusError: true,
  criteriaMode: 'all',
  mode: 'onSubmit',
  reValidateMode: 'onChange',
};

const CreateTest = () => {
  const firebaseHelper = getFirebaseBackend();
  const { t: translate } = useTranslation();
  const form = useForm(formConfig);
  const { formState, watch, getValues, setError, clearErrors } = form;
  const { user } = useAuth();
  const accountId = user?.account;
  const [testKey, setTestKey] = useState(null);
  const [successModal, setSuccessModal] = useState(false);

  const nextLessonKey = async (accountId) => {
    const newLessonKey = await firebaseHelper.newFirebaseKey('courses/', accountId);
    setTestKey(newLessonKey);
  };

  const closeModalHandle = () => {
    form.reset();
    setSuccessModal(false);
  };

  const onSubmit = async (data) => {
    if (!getValues('questions').length) {
      return setError('questions', {
        types: {
          required: 'At least one question is required!',
        },
      });
    }

    const processedQuestions = await Promise.all(
      data.questions.map(async (question) => {
        const questionKey = await firebaseHelper.newKey(`tests/${accountId}/${testKey}/questions`);
        return { ...question, id: questionKey };
      }),
    );

    let hasInvalidRegex = false;
    data.questions.forEach((question, index) => {
      if (question.type === 'code') {
        try {
          const pattern = question?.regex?.substring(1, question.regex.length);
          const flags = question?.flags?.substring(1, question.flags.length);
          new RegExp(pattern, flags);
        } catch (error) {
          console.error(error);
          hasInvalidRegex = true;
          setError(`questions.${index}.regex`, {
            types: {
              validate: 'Invalid Regex!',
            },
          });
        }
      }
    });
    if (hasInvalidRegex) return;

    const test = {
      accountId,
      id: testKey,
      name: data.name,
      passPercent: data.passPercent,
      percent: data.passPercent ? data.percent : null,
      scrambleQuestions: data.scrambleQuestions,
      answerKey: data.answerKey,
      questions: processedQuestions,
    };
    firebaseHelper.setFirebaseTest(test).then(() => {
      nextLessonKey(accountId);
      setSuccessModal(true);
    });
  };

  useEffect(() => {
    const questions = getValues('questions');
    if (questions.length) clearErrors('questions');
  }, [watch('questions')]);

  useEffect(() => {
    nextLessonKey();
  }, []);

  return (
    <PageStructure
      metaTitle="Create Test"
      cardTitle="Create a new Test"
      card
      breadcrumbItem="Create Test"
      breadcrumbTitle="Tests"
    >
      <SuccessModal
        isOpen={successModal}
        onClose={closeModalHandle}
        verb="Created"
        buttonText="See Tests"
        buttonLink="/tests"
      />

      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <LabelGroup htmlFor="name" label="Test Information" required>
            <TextInput controlName="name" placeholder="Test Name" mb={4} required />

            <SwitchInput controlName="passPercent" label="Minimum % to pass the test" />
            <ShowComponent condition={watch('passPercent')}>
              <LabelGroup htmlFor="percent" label="Define the minimum %" column>
                <SliderInput
                  controlName="percent"
                  min={0}
                  max={100}
                  step={10}
                  orientation="horizontal"
                  labels={{
                    0: '0',
                    10: '10',
                    20: '20',
                    30: '30',
                    40: '40',
                    50: '50',
                    60: '60',
                    70: '70',
                    80: '80',
                    90: '90',
                    100: '100',
                  }}
                />
              </LabelGroup>
            </ShowComponent>
            <SwitchInput controlName="scrambleQuestions" label="Scramble test questions" />
            <SwitchInput controlName="answerKey" label="Show answer key when user finishes test" />
          </LabelGroup>

          <LabelGroup htmlFor="questions" label="Questions" required>
            <QuestionsInput controlName="questions" />
          </LabelGroup>

          <Row className="justify-content-end">
            <Col lg="10">
              <Button
                type="submit"
                color="success"
                disabled={Object.values(formState.errors).length}
              >
                {translate('Create Test')}
              </Button>
            </Col>
          </Row>
        </form>
      </FormProvider>
    </PageStructure>
  );
};

CreateTest.displayName = 'Create Test Page';

export default CreateTest;
